import { useState } from 'react'
import { ParentalGate } from './parental-gate.jsx'

// First-launch paywall. V1 has no premium-locked content, so the
// "Continue free" link is always present — Apple rejects hard walls
// for Kids Category apps that don't deliver locked content. The
// Subscribe button is gated by the math problem; on success we stash
// the chosen plan in localStorage so future code can read it without
// hitting RevenueCat (real IAP wires up in V2).
export function Paywall({ onDismiss, onSubscribe }) {
  const [plan, setPlan] = useState('yearly')
  const [gateOpen, setGateOpen] = useState(false)

  const handleGateSuccess = () => {
    setGateOpen(false)
    onSubscribe?.(plan)
  }

  return (
    <div
      className="fixed inset-0 z-[70] flex flex-col overflow-hidden animate-page-modal"
      style={{ background: '#011140' }}
      role="dialog"
      aria-modal="true"
      aria-label="Subscribe to Moonpath"
    >
      <div
        className="flex-1 flex flex-col items-center justify-center px-6"
        style={{
          paddingTop: 'calc(env(safe-area-inset-top, 0px) + 24px)',
          paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)',
        }}
      >
        <img
          src="/logo-wordmark.png"
          alt="Moonpath"
          className="mx-auto animate-fade-up"
          style={{ width: 'min(60vw, 220px)', height: 'auto', animationDelay: '0.05s' }}
        />

        <h1
          className="text-white text-center font-bold leading-[1.15] tracking-[-0.02em] mt-6 animate-fade-up"
          style={{ fontSize: 'clamp(24px, 6.4vw, 30px)', animationDelay: '0.15s' }}
        >
          Unlock the full Moonpath
        </h1>
        <p
          className="text-center text-[15px] leading-[1.55] mt-3 mb-8 animate-fade-up"
          style={{ color: 'rgba(255,255,255,0.72)', maxWidth: 320, animationDelay: '0.25s' }}
        >
          Every story. Every ending. New adventures every week.
        </p>

        <div
          className="w-full max-w-sm space-y-3 mb-6 animate-fade-up"
          style={{ animationDelay: '0.35s' }}
        >
          <PlanCard
            label="Yearly"
            price="$39.99"
            sub="$3.33/month · billed yearly"
            badge="SAVE 33%"
            selected={plan === 'yearly'}
            onSelect={() => setPlan('yearly')}
          />
          <PlanCard
            label="Monthly"
            price="$4.99"
            sub="billed every month"
            selected={plan === 'monthly'}
            onSelect={() => setPlan('monthly')}
          />
        </div>

        <button
          type="button"
          onClick={() => setGateOpen(true)}
          className="btn-green w-full max-w-sm py-4 text-[17px] animate-fade-up"
          style={{ animationDelay: '0.45s' }}
        >
          Subscribe
        </button>

        <button
          type="button"
          onClick={onDismiss}
          className="mt-5 text-[14px] font-medium animate-fade-up"
          style={{ color: 'rgba(255,255,255,0.62)', animationDelay: '0.55s' }}
        >
          Continue free
        </button>

        <p
          className="mt-6 text-center text-[11px] leading-[1.5] animate-fade-up"
          style={{ color: 'rgba(255,255,255,0.4)', maxWidth: 320, animationDelay: '0.6s' }}
        >
          Cancel anytime. Subscription auto-renews unless turned off in your account settings.
        </p>
      </div>

      <ParentalGate
        open={gateOpen}
        onCancel={() => setGateOpen(false)}
        onSuccess={handleGateSuccess}
      />
    </div>
  )
}

function PlanCard({ label, price, sub, badge, selected, onSelect }) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className="relative w-full text-left rounded-2xl px-4 py-4 transition-all duration-200"
      style={{
        background: selected ? 'rgba(255,255,255,0.10)' : 'rgba(255,255,255,0.04)',
        border: `2px solid ${selected ? '#ffffff' : 'rgba(255,255,255,0.12)'}`,
      }}
      aria-pressed={selected}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-white font-bold text-[16px] leading-tight">{label}</p>
          <p
            className="text-[12px] leading-tight mt-1"
            style={{ color: 'rgba(255,255,255,0.55)' }}
          >
            {sub}
          </p>
        </div>
        <p className="text-white font-bold text-[20px] tabular-nums shrink-0">{price}</p>
      </div>
      {badge && (
        <div
          className="absolute -top-2 right-4 px-2 py-0.5 rounded-full text-[10px] font-bold tracking-[0.12em]"
          style={{ background: 'var(--color-mp-green-500)', color: '#0A2E14' }}
        >
          {badge}
        </div>
      )}
    </button>
  )
}
